<template>
  <div class="inline-block">
    <input
      :id="id"
      type="radio"
      :name="name"
      :value="value"
      :disabled="disabled"
      :checked="modelValue == value"
      tabindex="0"
      :required="required"
      :aria-labelledby="`${id}-label`"
      :aria-invalid="!!error"
      :aria-describedby="
        error ? `${id}-error` : helpLabel ? `${id}-help` : undefined
      "
      class="sr-only peer"
      @click="updateValue"
      @keydown.space.prevent="updateValue"
      @keydown.enter.prevent="updateValue"
    />
    <label
      :for="id"
      :class="[
        'inline-flex items-center',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
      ]"
    >
      <div
        class="relative switch-container"
        :class="{
          active: modelValue == value,
          disabled: disabled,
        }"
      >
        <div
          class="w-10 h-6 rounded-full shadow-inner transition-colors duration-300 ease-in-out"
          :class="{
            'bg-red-1': modelValue == value && !disabled,
            'bg-grey-13 dark:bg-dark-border': disabled || modelValue != value,
          }"
          role="presentation"
        ></div>
        <div
          class="dot absolute w-4 h-4 rounded-full shadow transition-transform duration-300 ease-in-out"
          :class="{
            'translate-x-4': modelValue == value,
            'bg-white': !disabled,
          }"
          :style="{ top: '0.25rem', left: '0.25rem' }"
          role="presentation"
        ></div>
      </div>
      <p
        :id="`${id}-label`"
        :class="[disabled ? 'text-grey-11 dark:text-dark-ink-muted' : '']"
        class="ml-3 text-base font-medium dark:text-dark-ink"
      >
        {{ label }} <AppRequiredLabel v-if="required" />
      </p>
    </label>
    <AppErrorLabel v-if="error" :id="`${id}-error`" class="mt-1"
      >{{ error }}
    </AppErrorLabel>
    <AppHelpLabel v-if="helpLabel" :id="`${id}-help`" class="mt-1"
      >{{ helpLabel }}
    </AppHelpLabel>
  </div>
</template>

<script setup>
const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean],
    default: '',
  },
  label: {
    type: String,
    required: true,
  },
  value: {
    type: [String, Number, Boolean],
    default: '',
  },
  name: {
    type: String,
    default: () => `switch-${Math.random().toString(36).substr(2, 9)}`,
  },
  id: {
    type: String,
    default: () => `switch-${Math.random().toString(36).substr(2, 9)}`,
  },
  allowUnselect: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
  },
  required: { type: Boolean, default: false },
  helpLabel: {
    type: String,
    default: '',
  },
  error: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:modelValue'])

const updateValue = () => {
  if (props.value == props.modelValue && props.allowUnselect) {
    emit('update:modelValue', null)
  } else {
    emit('update:modelValue', props.value)
  }
}
</script>

<style>
@reference "../../../../css/app.css";
.switch-container:not(.disabled):hover::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 25%;
  transform: translate(-50%, -50%);
  width: 50px;
  height: 50px;
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 50%;
  transition: left 0.3s;
}

.switch-container.active:not(.disabled):hover::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 80%;
  transform: translate(-50%, -50%);
  width: 50px;
  height: 50px;
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 50%;
  transition: left 0.3s;
}

input:focus-visible + label .switch-container {
  @apply ring-2 ring-red-1 ring-offset-2;
}
</style>
